import React, { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import { ArrowLeft, ShoppingBag, MessageCircle, Check } from "lucide-react";
import { api, imageUrl } from "../lib/api";
import { useCart } from "../context/CartContext";

export default function ProductDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [size, setSize] = useState("");
  const [wa, setWa] = useState("");
  const [notFound, setNotFound] = useState(false);
  const [activeImg, setActiveImg] = useState(0);

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      const p = r.data;
      setProduct(p);
      setActiveImg(0);
      const st = p.stock || {};
      const hasStock = Object.keys(st).length > 0;
      const firstAvail = (p.sizes || []).find((s) => (hasStock ? (st[s] || 0) > 0 : p.in_stock));
      setSize(firstAvail || "");
    }).catch(() => setNotFound(true));
    api.get("/config").then((r) => setWa(r.data.whatsapp)).catch(() => {});
  }, [id]);

  if (notFound) return <div className="pt-40 pb-40 text-center font-display text-3xl">Product not found. <Link to="/shop" className="text-[hsl(var(--primary))] underline">Back to shop</Link></div>;
  if (!product) return <div className="pt-40 pb-40 text-center font-body text-muted-foreground">Loading…</div>;

  const stock = product.stock || {};
  const hasStock = Object.keys(stock).length > 0;
  const availFor = (s) => (hasStock ? (stock[s] || 0) : (product.in_stock ? 99 : 0));
  const selectedAvail = availFor(size);
  const soldOut = !product.in_stock || (product.sizes?.length > 0 && product.sizes.every((s) => availFor(s) <= 0));

  const add = () => {
    if (product.sizes?.length && !size) { toast.error("Please select a size"); return; }
    if (selectedAvail <= 0) { toast.error("This size is out of stock"); return; }
    addItem(product, size);
    toast.success("Added to bag", { description: product.name });
  };

  const waMsg = encodeURIComponent(`Hi Navrang Udaipur, I'm interested in "${product.name}" (₹${product.price})${size ? `, size ${size}` : ""}.`);

  return (
    <div className="pt-24 pb-28">
      <div className="max-w-[1600px] mx-auto px-6 md:px-12">
        <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 overline text-muted-foreground mb-8 hover:text-foreground" data-testid="back-btn">
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Back
        </button>
        <div className="grid md:grid-cols-2 gap-px bg-border border border-border">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.8 }} className="bg-muted flex flex-col">
            <div className="overflow-hidden aspect-[3/4] md:flex-1">
              <img src={imageUrl((product.images || [])[activeImg])} alt={product.name} className="w-full h-full object-cover transition-opacity duration-300" data-testid="product-image" />
            </div>
            {(product.images || []).length > 1 && (
              <div className="flex gap-2 p-3 bg-background flex-wrap border-t border-border" data-testid="product-thumbnails">
                {product.images.map((img, i) => (
                  <button key={i} onClick={() => setActiveImg(i)} data-testid={`thumbnail-${i}`}
                    className={`w-16 h-20 overflow-hidden border-2 transition-colors ${activeImg === i ? "border-[hsl(var(--primary))]" : "border-transparent hover:border-border"}`}>
                    <img src={imageUrl(img)} alt={`${product.name} ${i + 1}`} className="w-full h-full object-cover" loading="lazy" />
                  </button>
                ))}
              </div>
            )}
          </motion.div>
          <div className="bg-background p-8 md:p-14 flex flex-col justify-center">
            <span className="overline text-[hsl(var(--primary))] capitalize">{product.category} · {product.fabric}</span>
            <h1 className="font-display text-4xl md:text-6xl leading-tight mt-4" data-testid="product-name">{product.name}</h1>
            <div className="flex items-baseline gap-3 mt-6">
              <span className="font-body text-2xl font-600">₹{product.price}</span>
              {product.original_price && <span className="font-body text-muted-foreground line-through">₹{product.original_price}</span>}
            </div>
            <p className="font-body text-muted-foreground mt-6 leading-relaxed max-w-md">{product.description}</p>

            {product.sizes?.length > 0 && (
              <div className="mt-8">
                <p className="overline mb-3">Size {selectedAvail > 0 && selectedAvail <= 3 && <span className="text-[hsl(var(--primary))]">· Only {selectedAvail} left</span>}</p>
                <div className="flex flex-wrap gap-2" data-testid="size-options">
                  {product.sizes.map((s) => {
                    const av = availFor(s);
                    return (
                      <button key={s} onClick={() => av > 0 && setSize(s)} disabled={av <= 0} data-testid={`size-${s}`}
                        className={`min-w-[3rem] px-3 py-2 font-body text-sm border transition-colors relative ${av <= 0 ? "border-border text-muted-foreground/40 line-through cursor-not-allowed" : size === s ? "bg-[hsl(var(--secondary))] text-white border-[hsl(var(--secondary))]" : "border-border hover:border-foreground"}`}>
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="mt-10 flex flex-col sm:flex-row gap-3">
              <button onClick={add} data-testid="add-to-cart-btn" disabled={soldOut || selectedAvail <= 0}
                className="flex-1 inline-flex items-center justify-center gap-2 bg-[hsl(var(--primary))] text-white px-8 py-4 font-body text-sm hover:bg-[hsl(var(--secondary))] transition-colors duration-400 disabled:opacity-50 disabled:cursor-not-allowed">
                {soldOut ? <><Check className="w-4 h-4" /> Sold out</> : selectedAvail <= 0 ? <><Check className="w-4 h-4" /> Size sold out</> : <><ShoppingBag className="w-4 h-4" strokeWidth={1.5} /> Add to bag</>}
              </button>
              {wa && (
                <a href={`https://wa.me/${wa}?text=${waMsg}`} target="_blank" rel="noreferrer" data-testid="whatsapp-order-btn"
                  className="inline-flex items-center justify-center gap-2 border border-border px-6 py-4 font-body text-sm hover:border-foreground transition-colors">
                  <MessageCircle className="w-4 h-4" strokeWidth={1.5} /> WhatsApp
                </a>
              )}
            </div>
            <p className="overline text-muted-foreground mt-8">Hand block-printed · Natural dyes · Ships across India</p>
          </div>
        </div>
      </div>
    </div>
  );
}
